import {noop} from '@benchling/js-utils';

import DocumentStore from 'notebook/documents/DocumentStore';
import {
  ProcedurePageContext,
  BlockingSaveObjectSource,
  BlockingSaveOperation,
} from 'procedures/ProcedurePageContext';
import runModal from 'ui-library/runModal';

import {MethodEditorTab} from './method-config/MethodConfigConstants';
import ProcedureConfirmExitWithUnsavedChangesModal, {
  ProcedureConfirmExitWithUnsavedChangesModalProps,
} from './modals/ProcedureConfirmExitWithUnsavedChangesModal';
import {ProcedureValidationContext} from './validation/ProcedureValidationContext';
import {ProcedureValidationObjectType} from './validation/ProcedureValidationTypes';
import {
  findParameterTableValidationErrors,
  findRelevantProcedureValidationErrors,
} from './validation/procedureValidationUtils';

/**
 * Checks if there are unsaved changes in a method editor tab and prompts the user to save their
 * changes before leaving the view. This should be called in flows where the user is able to navigate
 * away from the tab while still editing (i.e., isEditingMethodTab).

 * @param callbackFn - Callback is called in every path except when the user cancels the modal by clicking the "X". It has
 * a single boolean parameter didSave that indicates whether the user saved their changes or not.
 * * @returns A function that returns a promise that resolves to a boolean indicating whether the user took action that allows them to leave the method definitions tab.
 */
export default function useUnsavedTabChangesCheck(
  currentMethodEditorTab?: MethodEditorTab
): (callbackFn: (didSave: boolean) => void) => void {
  const {
    isEditingMethodTab,
    setIsEditingMethodTab,
    legacyIsProcedureDirty,
    selectedMethodCid,
    legacySaveImmediately,
    resetDraftFlowchartChanges,
  } = ProcedurePageContext.useContext();
  const {procedureValidationErrors, anyTabValidationErrors} = ProcedureValidationContext.useContext();

  return (callbackFn: (didSave: boolean) => void) => {
    const isTabClean = !isEditingMethodTab || !legacyIsProcedureDirty;
    const noMethodIsSelected = selectedMethodCid == null;

    if (isTabClean || noMethodIsSelected) {
      setIsEditingMethodTab(false);
      callbackFn(false);
      return;
    }

    const selectedMethodValidationErrors = findRelevantProcedureValidationErrors(procedureValidationErrors, {
      id: selectedMethodCid,
      objectType: ProcedureValidationObjectType.METHOD,
    });
    const parameterTableValidationErrors = findParameterTableValidationErrors(
      selectedMethodValidationErrors,
      selectedMethodCid
    );
    runModal<ProcedureConfirmExitWithUnsavedChangesModalProps>(ProcedureConfirmExitWithUnsavedChangesModal, {
      onRequestClose: noop,
      onRequestLeaveWithoutSaving: () => {
        resetDraftFlowchartChanges();
        setIsEditingMethodTab(false);
        setTimeout(() => {
          callbackFn(false);
        }, 0);
      },
      onRequestSubmit: async () => {
        // This call requires an immediate API call, with an overlay
        // TODO:(BNCH-140082): Conditionally use powSaveImmediately
        await legacySaveImmediately(
          () => ({
            blockingSaveObjectSource: BlockingSaveObjectSource.Procedure,
            blockingSaveOperation: BlockingSaveOperation.SaveUnsubmittedOnExit,
          }),
          'Submitting changes...'
        );
        // When saving Parameter Definitions, we need to clear the saved documents in memory so
        // that the user fetches them with any new changes applied
        DocumentStore.resetState();
        setIsEditingMethodTab(false);
        setTimeout(() => {
          callbackFn(true);
        }, 0);
      },
      isEditingMethodTab: true,
      currentMethodEditorTab,
      anyErrorsInTables: parameterTableValidationErrors.length > 0 || anyTabValidationErrors,
    });
  };
}
